import React, { useMemo } from 'react';
import { concat, intersection } from 'lodash';
import useModulePermissions from 'hooks/useModulePermissions';
import { ModuleRouterContext } from 'contexts/ModuleRouterContext';
import { ModulesPermissions } from 'utils/enums';
import { MODULE_PERMISSIONS } from 'utils/constants';
import { POSLoader } from 'components/POSLoader/POSLoader';
import { COCKPIT_MODULE_NAME, WEBFRAME_MODULE_NAME } from 'utils/permissions';

interface ModuleRouterProviderProps {
  children?: React.ReactNode;
}

export const ModuleRouterProvider = (props: ModuleRouterProviderProps) => {
  const { grantedModulePermissionKeys, isLoading } = useModulePermissions();

  const grantedPermissionKeys = useMemo(() => {
    let keys: string[] = [];

    if (!isLoading) {
      [WEBFRAME_MODULE_NAME, COCKPIT_MODULE_NAME].forEach(
        (module: string) => (keys = concat(keys, grantedModulePermissionKeys(module)))
      );
    }

    return keys;
  }, [grantedModulePermissionKeys, isLoading]);

  const modulesContext = useMemo(() => {
    if (!grantedPermissionKeys.length) return [];

    const modulesPermissions: ModulesPermissions[] = [
      ModulesPermissions.COCKPIT,
      ModulesPermissions.WEBFRAME_HEADER_MAKE_IT_RIGHT_AUDIENCE,
      ModulesPermissions.WEBFRAME_HEADER_MAKE_IT_RIGHT_BRIEF,
      ModulesPermissions.WEBFRAME_HEADER_MAKE_IT_RIGHT_PATH_TO_GROWTH,
      ModulesPermissions.WEBFRAME_HEADER_MAKE_IT_MAGIC_DESIGN,
      ModulesPermissions.WEBFRAME_HEADER_MAKE_IT_MAGIC_INSPIRE,
      ModulesPermissions.WEBFRAME_HEADER_MAKE_IT_WORK_ACTIVATE,
      ModulesPermissions.WEBFRAME_HEADER_MAKE_IT_WORK_OPTIMIZE_AND_REPORT,
    ];
    const modules = intersection(modulesPermissions, grantedPermissionKeys);
    const moduleNames = modules.map((module) => MODULE_PERMISSIONS[module]);

    return moduleNames;
  }, [grantedPermissionKeys]);

  return (
    <ModuleRouterContext.Provider value={modulesContext}>
      {isLoading && <POSLoader showLoadingText={false} />}
      {!isLoading && props.children}
    </ModuleRouterContext.Provider>
  );
};
